import { Node, mergeAttributes } from '@tiptap/core';

export interface VariablesPlaceholderOptions {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  HTMLAttributes: Record<string, any>;
  variables: string[];
}

declare module '@tiptap/core' {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Commands<ReturnType> {
    variablesPlaceholder: {
      /**
       * Insert a variable placeholder
       */
      insertVariable: (variable: string) => ReturnType;
      /**
       * Set variables placeholder mark
       */
      setVariablesPlaceholder: (variable: string) => ReturnType;
      /**
       * Unset variables placeholder mark
       */
      unsetVariablesPlaceholder: () => ReturnType;
      /**
       * Convert existing variables to placeholder nodes
       */
      convertExistingVariables: () => ReturnType;
    };
  }
}

export const variablesPlaceholderExtension = Node.create<VariablesPlaceholderOptions>({
  name: 'variablesPlaceholder',

  group: 'inline',
  inline: true,
  atom: true,

  addOptions() {
    return {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      HTMLAttributes: {
        class: 'tiptap-variable-placeholder',
      },
      variables: [],
    };
  },

  addAttributes() {
    return {
      variable: {
        default: null,
        parseHTML: (element): string | null => {
          const content = element.textContent || '';
          // Extraire la variable de [[variable]] ou {{variable}} (compatibilité Quill)
          const newFormatMatch = content.match(/^\[\[(.+)\]\]$/);
          const oldFormatMatch = content.match(/^\{\{(.+)\}\}$/);
          const variable = newFormatMatch ? newFormatMatch[1] : oldFormatMatch ? oldFormatMatch[1] : null;

          // Si on a trouvé une variable, on la retourne
          // Sinon, on essaie de voir si le contenu est juste le nom de la variable
          return variable || content.trim() || null;
        },
        renderHTML: (attributes): Record<string, any> | false => {
          if (!attributes.variable) {
            return {};
          }
          return {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'data-variable': attributes.variable,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'inserts',
        getAttrs: (element): Record<string, any> | false => {
          if (typeof element === 'string') {
            return false;
          }

          const content = element.textContent || '';
          // Support des deux formats : [[variable]] et {{variable}} (compatibilité Quill)
          const newFormatMatch = content.match(/^\[\[(.+)\]\]$/);
          const oldFormatMatch = content.match(/^\{\{(.+)\}\}$/);

          const variable = newFormatMatch ? newFormatMatch[1] : oldFormatMatch ? oldFormatMatch[1] : null;

          if (variable) {
            return {
              variable,
            };
          }

          return false;
        },
      },
    ];
  },
  // eslint-disable-next-line @typescript-eslint/naming-convention
  renderHTML({ HTMLAttributes, node }) {
    const variable = node.attrs.variable;

    return [
      'inserts',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'data-variable': variable,
        contenteditable: 'false',
        class: 'tiptap-variable-placeholder',
      }),
      `[[${variable}]]`,
    ];
  },

  addCommands() {
    return {
      insertVariable:
        (variable: string) =>
        ({ commands }): boolean => {
          if (!variable) {
            return false;
          }

          // Insérer le node de variable suivi d'un espace
          return commands.insertContent([
            {
              type: this.name,
              attrs: { variable },
            },
            {
              type: 'text',
              text: ' ',
            },
          ]);
        },

      setVariablesPlaceholder:
        (variable: string) =>
        ({ commands }): boolean => {
          if (!variable) {
            return false;
          }

          return commands.insertContent({
            type: this.name,
            attrs: { variable },
          });
        },

      unsetVariablesPlaceholder:
        () =>
        ({ commands }): boolean =>
          commands.deleteSelection(),

      convertExistingVariables:
        () =>
        ({ commands }): boolean => {
          // Obtenir le contenu HTML actuel
          const currentHTML = this.editor.getHTML();

          // Diviser le HTML en segments pour éviter de convertir ce qui est déjà dans des <inserts>
          const segments = currentHTML.split(/(<inserts[^>]*>.*?<\/inserts>)/g);

          let hasChanges = false;

          // Traiter chaque segment
          const processedSegments = segments.map(segment => {
            // Si c'est un segment <inserts>, le laisser tel quel
            if (segment.startsWith('<inserts')) {
              return segment;
            }

            // Sinon, chercher et remplacer les [[variable]]
            const newSegment = segment.replace(/\[\[([^\]]+)\]\]/g, (match, variable) => {
              // Ignorer meanLink complètement
              if (variable === 'meanLink') {
                return match;
              }
              hasChanges = true;
              return `<inserts class="tiptap-variable-placeholder" data-variable="${variable}" contenteditable="false">[[${variable}]]</inserts>`;
            });

            return newSegment;
          });

          // Si des changements ont été effectués, mettre à jour le contenu
          if (hasChanges) {
            const newHTML = processedSegments.join('');
            commands.setContent(newHTML);
            return true;
          }

          return false;
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      // Empêcher l'édition des variables avec les touches
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Backspace: ({ editor }): boolean => {
        const { selection } = editor.state;
        const { $from } = selection;

        // Vérifier si on est dans une variable
        const mark = $from.marks().find(m => m.type.name === this.name);
        if (mark) {
          // Supprimer toute la variable
          const from = $from.pos - $from.textOffset;
          const to = from + $from.parent.child($from.index()).nodeSize;
          editor.chain().deleteRange({ from, to }).run();
          return true;
        }

        return false;
      },
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Delete: ({ editor }): boolean => {
        const { selection } = editor.state;
        const { $from } = selection;

        // Vérifier si on est juste avant une variable
        const nextPos = $from.pos + 1;
        if (nextPos <= editor.state.doc.content.size) {
          const $next = editor.state.doc.resolve(nextPos);
          const mark = $next.marks().find(m => m.type.name === this.name);

          if (mark) {
            // Supprimer toute la variable
            const from = nextPos - $next.textOffset;
            const to = from + $next.parent.child($next.index()).nodeSize;
            editor.chain().deleteRange({ from, to }).run();
            return true;
          }
        }

        return false;
      },

      // Empêcher la navigation dans les variables
      // eslint-disable-next-line @typescript-eslint/naming-convention
      ArrowLeft: ({ editor }): boolean => {
        const { selection } = editor.state;
        const { $from } = selection;

        const mark = $from.marks().find(m => m.type.name === this.name);
        if (mark) {
          // Aller au début de la variable
          const from = $from.pos - $from.textOffset;
          editor.chain().setTextSelection(from).run();
          return true;
        }

        return false;
      },

      // eslint-disable-next-line @typescript-eslint/naming-convention
      ArrowRight: ({ editor }): boolean => {
        const { selection } = editor.state;
        const { $from } = selection;

        const mark = $from.marks().find(m => m.type.name === this.name);
        if (mark) {
          // Aller à la fin de la variable
          const from = $from.pos - $from.textOffset;
          const to = from + $from.parent.child($from.index()).nodeSize;
          editor.chain().setTextSelection(to).run();
          return true;
        }

        return false;
      },
    };
  },

  // Ajout d'un storage pour les variables disponibles
  addStorage() {
    return {
      variables: this.options.variables,
      convertTimeout: null,
    };
  },

  onCreate() {
    // Convertir les [[variable]] au chargement
    setTimeout(() => {
      this.editor.commands.convertExistingVariables();
    }, 100);
  },

  onUpdate() {
    // Ne convertir que si l'utilisateur a terminé de taper une variable complète
    const currentHTML = this.editor.getHTML();

    // Vérifier s'il y a des variables complètes qui viennent d'être fermées
    const hasCompleteVariables = /\[\[([^\]]+)\]\]/.test(currentHTML);

    // Vérifier s'il y a des variables incomplètes en cours de saisie
    const hasIncompleteVariables = /\[\[([^\]]*$)/.test(currentHTML.replace(/\[\[([^\]]+)\]\]/g, ''));

    // Ne convertir que s'il y a des variables complètes ET pas de variables en cours de saisie
    if (hasCompleteVariables && !hasIncompleteVariables) {
      clearTimeout(this.storage.convertTimeout);
      this.storage.convertTimeout = setTimeout(() => {
        this.editor.commands.convertExistingVariables();
      }, 500);
    }
  },
});
